import { isReservedInstanceSlug } from "./library/library-instance-slug.js";
import { RESERVED_APP_SCOPES } from "./event-subjects.js";
import { RESERVED_PLATFORM_PREFIX_SLUGS } from "./reserved-platform-prefixes.js";
import { RESOURCE_SLUG_PATTERN } from "./slug-grammar.js";
import { appSourceInputSchema } from "./app-source.js";
import { z } from "zod";
//#region src/app-admin-inputs.ts
/**
* Shared zod input/query schemas for app-administration operations (MDEC-3).
*
* Single source of truth for the request + query shapes of the app ops that had
* NO shared contract: create app, add app owner, update app metadata, and the
* adoption-matrix query filter. The app-dev MCP server's tools source their
* `inputSchema` from here (M4); the CP controllers that declare these inline
* today (release-engine `apps.controller`, site-admin-api `app-admin.controller`)
* converge on these same schemas so validation cannot drift between the MCP tool
* surface and the endpoint (see the migration note in ws-mcp-core.md §7).
*
* These schemas mirror the CURRENT server DTOs verbatim — wire truth, not a new
* proposal. Any field a consumer needs beyond what the live endpoint accepts is
* an ENDPOINT change (escalated), never silently added here.
*
* The three app-VERSION DTOs are out of scope — they already live in
* `@zion/sdk-cp-dp-contracts`.
*/
/**
* The app-slug grammar. **ONE declaration, platform-wide (#1107).**
*
* ⛔ **DECLARED HERE ONLY — do not re-declare it, and do not re-type the literal
* anywhere.** Until 2026-08-04 this exact regex existed as three independent
* non-exported `const`s: here, in release-engine's `create-app.dto.ts`, and
* inline in the hosted MCP `resolve_app` tool's schema. All three were
* character-identical and **nothing kept them so** — the same duplicated-invariant
* shape as the two `SLUG_MESSAGE`/`RESERVED_APP_SLUG_MESSAGE` copies that CR-8
* collapsed for the same reason.
*
* ⭐ Why a re-typed literal is worse than useless as a "pin": two regexes with the
* same source are `!==`, so a test comparing them by `.source` passes while they
* are separate objects that can drift independently on the next edit. Consumers
* import THIS object, and the `resolve_app` spec asserts **referential identity**
* (`toBe`) against it — a constraint a re-typed literal cannot satisfy.
*
* ⚠️ Deliberately NOT anchored to a nullable/optional wrapper and NOT `/g`
* flagged: `.test()` on a `/g` regex is stateful across calls and would alternate
* true/false on identical input.
*
* ⚠️ It is intentionally permissive about UUID shape — every lowercase UUID
* satisfies it — which is why the create-time door pairs it with the separate
* {@link APP_ID_UUID_PATTERN} refusal. A consumer that only needs the *grammar*
* (e.g. the MCP `resolve_app` slug argument, which never creates anything) takes
* this pattern alone and must NOT mirror that refine.
*
* ⛔ **AND IT IS AN ALIAS NOW, NOT A DECLARATION (#1036).** The grammar itself moved DOWN to
* `slug-grammar.ts` as {@link RESOURCE_SLUG_PATTERN}, because this rule's own text — *"ONE
* declaration, platform-wide"* — was being broken by FIVE sites, one of them 460 lines away in
* this same package, and because the rule was scoped to *"the app-slug grammar"* while tenant
* slugs, org slugs, OAuth client ids and machine-principal slugs all needed the identical
* language. This name and this docblock survive so every existing consumer and this file's barrel
* entry are byte-unchanged. ⚠️ It is REFERENTIALLY IDENTICAL to `RESOURCE_SLUG_PATTERN`, which is
* what keeps the `resolve_app` spec's `toBe` assertion valid.
*/
const SLUG_PATTERN = RESOURCE_SLUG_PATTERN;
/**
* The rejection message for {@link SLUG_PATTERN}. **ONE declaration (#1107).**
*
* ⛔ Exported for the SAME reason as the pattern, and it is the rule this file
* already states about its other two refines: *"the PATTERN and the MESSAGE are
* imported, never re-declared, so this door and the contracts copy reject
* IDENTICALLY"* (CR-8). Until 2026-08-04 the pattern obeyed that rule and the
* message did not — it existed as three identical `const`s, so a reworded
* rejection would have reached one door and not the others, and a caller would
* have seen two different explanations for the same refusal depending on which
* surface it hit.
*/
const SLUG_MESSAGE = "slug must be lowercase alphanumeric with dashes (no leading/trailing dash)";
/**
* Platform-reserved app slugs (DEC-S4-d-READLEG). An app slugged with one of
* these would be auto-granted that platform stream's subtree AND its JetStream
* consumer-API family: `scaling` → `zion.scaling.>` + `$JS.…ZION_SCALING.>`
* (every DP's scaling events), `dlq` → `zion.dlq.>` + `$JS.…ZION_DLQ.>`,
* `core-api` → the `ZION_CORE-API` system stream. Defense-in-depth alongside the
* `isNatsSubjectWithinAppScope` scope+subject rejects (§2.7 a) and the composer
* skip (§2.7 c). The SET is imported, NEVER re-declared — one SoT (§2.3).
* DECLARED HERE ONLY; the release-engine DTO IMPORTS it (never re-declares).
*/
const RESERVED_APP_SLUG_MESSAGE = "slug is platform-reserved and cannot be used as an app slug";
/**
* The shape that makes a `:appId`/`:id` path parameter an app **id**.
*
* ⛔ Every CP app-id resolver (release-engine `AppsService.get`,
* `AppChannelPinsService.requireApp`, `AppCompositionService.resolveApp`) treats
* a parameter of this shape as an **id ONLY — never with a slug fallback**.
* `ResourcePermissionGuard` authorizes `req.params.*` verbatim, so a UUID→slug
* fallback could hand a handler a DIFFERENT app than the guard authorized — the
* cross-resource-IDOR shape confirmed in R2.
*
* DECLARED HERE ONLY, so the create-time door below and those resolvers test
* the SAME shape. If they diverged, a slug could be created that a resolver
* later reads as an id, which is precisely the ambiguity the rule removes.
*
* No `g` flag: `.test()` on a `/g` regex is stateful across calls and would
* alternate true/false on identical input.
*/
const APP_ID_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
* App slugs that name a one-segment literal route of release-engine's `v1/apps` controller:
* `GET /v1/apps/orphaned-grants` and `GET /v1/apps/ring-versions` (plan
* 2026-10-07-app-source-record, R-015). Nest matches the literal route before `@Get(':id')`, so
* `GET /v1/apps/:id` never reaches an app with one of these slugs by slug. A slug is immutable, so
* the create door refuses it. This file declares the set; the release-engine DTO imports it.
*/
const RESERVED_APPS_ROUTE_SLUGS = /* @__PURE__ */ new Set(["orphaned-grants", "ring-versions"]);
/** Rejection message for a slug in {@link RESERVED_APPS_ROUTE_SLUGS}. */
const RESERVED_APPS_ROUTE_SLUG_MESSAGE = "slug names a /v1/apps route (orphaned-grants, ring-versions) and cannot be used as an app slug";
/**
* Rejection message for a UUID-shaped app slug (R2 code-review C11, the near
* half of **#855**).
*
* `SLUG_PATTERN` is `[a-z0-9-]`, which **every lowercase UUID satisfies**, so
* without an explicit refusal an app can be created with a UUID-shaped slug.
* Such an app stays fully manageable **by its id** — what breaks is
* slug-addressing that one app: `GET/PUT /v1/apps/<uuid-shaped-slug>/channel-pins/…`
* and `POST /v1/releases/apps/<uuid-shaped-slug>/versions` 404 permanently, and
* a slug is immutable (`updateAppSchema` does not accept one), so there is no
* in-product repair. The blast radius is "the canonical slug stops working for
* an app nobody should have been able to create", not a stranded app.
*
* DECLARED HERE ONLY; the release-engine DTO IMPORTS it (never re-declares) —
* same rule, and same reason, as {@link RESERVED_APP_SLUG_MESSAGE}.
*
* ⚠️ Closes the door on NEW rows only. Pre-existing rows are #855's remaining
* half; until then the resolvers' 404 names the ambiguity so such a row is at
* least diagnosable.
*/
const UUID_SHAPED_APP_SLUG_MESSAGE = "slug must not be UUID-shaped: a UUID-shaped path parameter is resolved as an app id only (never as a slug), so such an app could never be addressed by slug";
/**
* Rejection message for a slug whose OWN namespace is a platform-reserved prefix (**#1748**).
*
* ⛔ **THE TWO LISTS SHARED NO MEMBER, AND #1733 TURNED THAT INTO AN UNREPAIRABLE APP.**
* `RESERVED_APP_SCOPES` (`scaling` · `dlq` · `core-api` · `audit` · `satellite`) is about NATS subject subtrees;
* {@link RESERVED_PLATFORM_PREFIX_SLUGS} is about `/v1/*` HTTP routing (`auth` · `setup` · `admin` ·
* `release` · `platform` · `apps` · `fixtures` · `me` · `internal`). Before this refine
* `POST /v1/apps {slug:'admin'}` was ACCEPTED. Since #1733 every apiPath such an app declares must be
* `/v1/admin` or beneath it — and `rejectReservedOverlap` refuses every one of those. So the app
* could never publish ANY apiPath, and it would learn that as two unrelated refusals from two
* different doors, neither naming the cause.
*
* ⛔ **A slug is IMMUTABLE** (`updateAppSchema` takes no `slug`), so there is no
* in-product repair once the row exists — which is why the fix must be at the create door and why
* this message states the REASON rather than only the rule. Measured 2026-08-17: no live app holds
* one of the nine, so this is preventive, not a migration.
*
* DECLARED HERE ONLY; the release-engine DTO IMPORTS it (never re-declares) — same rule, and same
* reason, as {@link RESERVED_APP_SLUG_MESSAGE} and {@link UUID_SHAPED_APP_SLUG_MESSAGE} (CR-8).
*/
const RESERVED_PLATFORM_PREFIX_SLUG_MESSAGE = "slug is a platform-reserved API prefix: every apiPath an app declares must be under '/v1/<slug>', and '/v1/<this slug>' is owned by the platform — so such an app could never declare a compliant apiPath, and a slug cannot be changed afterwards";
/**
* The refusal message of a slug that starts with `zl-` (2026-10-12-shared-libraries, R-002, R-013).
* The CP allocates every library instance as a `release.apps` row under a `zl-` slug that
* `libraryInstanceSlug` derives, so an app that took such a slug first would collide with an
* instance, and a slug cannot be changed afterwards. Declared here only. The contract
* {@link createAppSchema} uses it. The release-engine DTO `create-app.dto.ts`, the one door that
* creates an app row (the site-admin-api MCP `create_app` tool forwards to it), does not import it
* yet; WS-18 adds the import and the refusal there in stage 2.
*/
const LIBRARY_INSTANCE_SLUG_RESERVED_MESSAGE = "slug starts with 'zl-', the prefix the platform reserves for library instances, and a slug cannot be changed afterwards";
/**
* The app roles that a caller may grant on one app, in descending authority.
*
* Callers: the release-engine add-owner route (`POST /v1/apps/:appId/owners`, through the
* release-engine copy of `addAppOwnerSchema`), the site-admin-api MCP tool `add_app_owner`, and
* the role picker and role order of the CP console Access pane. Each takes the list from here,
* directly or through {@link appRoleSlugEnum}. None re-declares it.
*
* The three slugs, each a per-app role in cp-auth-api's `role_definitions`:
* - `app_owner`: the full set on the app, the delete verb and the licence offer included.
* - `app_maintainer`: the developer set on one app (2026-10-20-cp-permissioning D2). Before that
*   plan, the per-app grant of `app_developer` carried this set. Migration 0049 moves every such
*   grant to `app_maintainer`, and `app_developer` becomes a site role that no app grant names.
* - `app_viewer`: the read set on the app.
*
* Stage 3 of 2026-10-20-cp-permissioning removed `app_admin` (R-021 item 3). Migration 0050 maps
* every `app_admin` grant to `app_owner`, and migration 0051 deletes the role row. The schema
* refuses `app_admin` with a zod `invalid_value` issue, as it refuses every other slug.
*
* The order is the authority order: `app_owner` first and `app_viewer` last. The console sorts
* grants by it. A new slug takes its place by authority, never at the end by default. `app_owner`
* holds every permission of `app_maintainer`, and `app_maintainer` every permission of
* `app_viewer` (R-005), so an owner can grant the two roles below it under the grantor ceiling.
*
* Invariant: no site role is in the list. `app_developer` stays out, so the add-owner route
* refuses it with 400. `ResourceGrantsService.create` also refuses a resource grant of a seeded
* role whose `is_resource_scoped` is false (R-003, R-008); a custom role keeps its behaviour.
*/
const APP_ROLE_SLUGS = [
	"app_owner",
	"app_maintainer",
	"app_viewer"
];
/**
* The zod enum of {@link APP_ROLE_SLUGS}. It accepts one of the three slugs and refuses every
* other value, a site role slug and the removed `app_admin` included, with a zod `invalid_value`
* issue. It has no transform, so `createOpenApiDocument()` can render it.
*/
const appRoleSlugEnum = z.enum(APP_ROLE_SLUGS);
/** Ring axis enum — matches `Ring` from `./rings.js`. */
const ringEnum = z.enum([
	"stable",
	"beta",
	"alpha",
	"edge"
]);
/** Health enum — matches the cp-dp fixture `HealthSchema`. */
const healthEnum = z.enum([
	"healthy",
	"degraded",
	"unhealthy"
]);
/** Adoption page bounds — pinned to the `FetchCpClient` filter shape (R-B2), NOT the
*  site-admin DTO's 50/200. Default 100, hard-cap 1000. */
const ADOPTION_DEFAULT_LIMIT = 100;
const ADOPTION_MAX_LIMIT = 1e3;
const VERSION_MAX = 256;
const createAppSchema = z.object({
	slug: z.string().min(2).max(63).regex(SLUG_PATTERN, { message: SLUG_MESSAGE }).refine((s) => !RESERVED_APP_SCOPES.has(s), { message: RESERVED_APP_SLUG_MESSAGE }).refine((s) => !RESERVED_PLATFORM_PREFIX_SLUGS.has(s), { message: RESERVED_PLATFORM_PREFIX_SLUG_MESSAGE }).refine((s) => !APP_ID_UUID_PATTERN.test(s), { message: UUID_SHAPED_APP_SLUG_MESSAGE }).refine((s) => !RESERVED_APPS_ROUTE_SLUGS.has(s), { message: RESERVED_APPS_ROUTE_SLUG_MESSAGE }).refine((s) => !isReservedInstanceSlug(s), { message: LIBRARY_INSTANCE_SLUG_RESERVED_MESSAGE }).describe("URL-safe app slug (lowercase, alphanumeric + dashes; not UUID-shaped; not zl-)."),
	name: z.string().min(1).max(120).describe("Human-readable display name."),
	description: z.string().optional().describe("Free-text description."),
	ownerOperatorId: z.string().uuid().describe("User id of the initial app owner. Written to cp_auth.resource_grants BEFORE the apps row."),
	source: appSourceInputSchema.optional().describe("Where the app code lives, as the CLI detected it from the checkout. The CP sets detectedAt.")
}).strict();
const addAppOwnerSchema = z.object({
	principalId: z.string().uuid().describe("User id to grant the role to."),
	roleSlug: appRoleSlugEnum.describe("Role slug, one of 'app_owner', 'app_maintainer', 'app_viewer'.")
}).strict();
const updateAppSchema = z.object({
	name: z.string().min(1).max(120).optional().describe("Human-readable display name."),
	description: z.string().nullable().optional().describe("Free-text description; null clears it, omitted leaves it unchanged."),
	source: appSourceInputSchema.optional().describe("A new app source record that replaces the stored one; omitted leaves it unchanged.")
}).strict();
const adoptionQuerySchema = z.object({
	ring: ringEnum.optional(),
	version: z.string().min(1).max(VERSION_MAX).optional(),
	health: healthEnum.optional(),
	/** Org filter — `z.string()` (NOT `.uuid()`), matching the `FetchCpClient`
	*  adoption filter (R-B2). Do not tighten to `.uuid()`. */
	orgId: z.string().optional(),
	/** Opaque base64url keyset cursor from the previous page's response. */
	cursor: z.string().min(1).max(VERSION_MAX).optional(),
	/** Query-string coercion: `"100"` → `100`. Bounded `[1, 1000]`, default 100 (R-B2). */
	limit: z.coerce.number().int().positive().max(ADOPTION_MAX_LIMIT).default(100)
}).strict();
/** Default + max page size, mirroring the E2 spec's `[1,200]`, default 50. */
const DEPLOYMENTS_DEFAULT_LIMIT = 50;
const DEPLOYMENTS_MAX_LIMIT = 200;
/** Default + max page size for the transitions keyset page (`design/02` §2.2 E11). */
const HISTORY_DEFAULT_LIMIT = 200;
const HISTORY_MAX_LIMIT = 500;
/**
* DNS-1123 label, verbatim from the spec. `dp_fixture_status.fixture_id` is a
* k8s object-name component, so this is the producer's own domain rather than a
* CP invention — an input outside it can match no stored row.
*/
const FIXTURE_ID_PATTERN = /^[a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?$/;
/**
* Version slug, verbatim from the spec (`MAX_VERSION_SLUG_LENGTH = 16`).
* Bounded at the schema so the `max(16)` and the pattern agree — the pattern
* alone is unbounded and `.max()` alone admits `A`/`_`.
*
* ⛔ Renamed from `VERSION_SLUG_PATTERN` on promotion. A DIFFERENT
* `VERSION_SLUG_PATTERN` (`/^v[a-z0-9](…)*$/`, the producer's generated-slug
* grammar) lives in `@zion/sdk-cp-dp-contracts`; 40+ files import both
* packages, so one name over two regexes would bind silently and typecheck.
* This one is the deliberately looser **query filter** grammar — it accepts
* values the producer can never generate (`0`, `-`), which is correct for a
* filter and wrong for a validator.
*/
const VERSION_SLUG_FILTER_PATTERN = /^[a-z0-9-]{1,16}$/;
/**
* Maximum length of a version slug (E2 spec).
*
* ⚠️ Same name and same value as `@zion/sdk-cp-dp-contracts`' export,
* deliberately, and the same concept. Contracts may not import cp-dp
* (SDK-002), so this is the `DEFAULT_TAIL_LINES` precedent: two declarations,
* pinned by value assertions on both sides. If you change one, change both.
*/
const MAX_VERSION_SLUG_LENGTH = 16;
/**
* Alias of {@link healthEnum}, which is byte-identical. Bound by identity, not
* copied: a second `z.enum([...])` here would be the two-copy drift this file
* exists to prevent.
*
* Health axis for E2's filter — **exactly** the producer's domain.
*
* ⚠️ The E2 spec lists a fourth member, `'unknown'`. It is deliberately NOT
* here. The wire contract is `HealthSchema = z.enum(['healthy','degraded',
* 'unhealthy'])` (`@zion/sdk-cp-dp-contracts` `status-report.schemas.ts`) and
* the StatusReport ingest is zod-validated before persist, so
* `release.dp_fixture_status.health` cannot hold `'unknown'`. Accepting it would
* mean `?health=unknown` returns an empty page **forever** while looking like a
* legitimate filter — a permanently-unpopulated arm is a trap, not a courtesy.
* "No report" is not a value of this field; it is the absence of a row.
*
* Mirrors `AppDeploymentHealth` in this package; the two must move together.
*/
const deploymentHealthEnum = healthEnum;
/**
* Query-string schema for **E2** — `GET /v1/admin/apps/:appId/deployments`
* (R4, `design/02-observability-and-status.md` §2.2 E2).
*
* E2 is the sibling of `GET :appId/adoption` at the `(dp, fixture, version)`
* grain rather than `(org, dp, version)`, so this schema deliberately mirrors
* `adoptionQuerySchema` above — same `.strict()` posture, same coerced+bounded
* `limit`, same opaque-cursor treatment — and diverges only where the grain
* does.
*
* **Why `.strict()` is the security control, not a nicety.** The spec's
* "Rejects" list for E2 is `orgId`, free-text sort keys, and `offset`:
*   - `orgId` is derived server-side from the authenticated principal and is
*     **never** accepted from the client (C2 of the design synthesis) — an
*     accepted `?orgId=` is the authz-bypass-via-query-param finding in
*     CLAUDE.md's bot fix-catalog.
*   - a free-text sort key would be interpolated into an `ORDER BY`.
*   - `offset` would defeat the keyset cursor's stability guarantee.
* `.strict()` rejects all three (and anything else) with a 400 by construction,
* so the list needs no per-key arm — but it is enumerated in the tests, because
* a `.strict()` that someone later relaxes to `.passthrough()` fails silently.
*
* The global `ZionStrictValidationPipe` binds this at the controller via
* `@Query({ schema })` and deep-stricts it, so an unknown key at any depth is a
* `400` before the service — in particular `?dpId=foo` would otherwise reach the
* SQL layer's `::uuid` cast and surface as a 500 (a DB-error leak), and
* `?limit=foo` would produce `LIMIT NaN`.
*/
const deploymentsQuerySchema = z.object({
	/** Narrow to one data plane. A DP the app does not run on yields an empty
	*  page — it is a filter, never a proxy target, so it needs no membership
	*  check and leaks nothing beyond "no rows". */
	dpId: z.string().uuid().optional(),
	fixtureId: z.string().regex(FIXTURE_ID_PATTERN).optional(),
	versionSlug: z.string().max(16).regex(VERSION_SLUG_FILTER_PATTERN).optional(),
	health: deploymentHealthEnum.optional(),
	ring: ringEnum.optional(),
	/**
	* B7 — the version-state stat tile, as a SERVER-SIDE filter.
	*
	* ⛔ ONE ENUM, NOT THREE BOOLEANS. `AppFleetPane.tsx:324` names the missing
	* fields as `inSync` / `drifted` / `noDesired`, but three independent
	* booleans make `?inSync=true&drifted=true` representable — a combination
	* that can never match a row and that the caller has no way to know is
	* empty-by-construction. The three are mutually exclusive states of ONE
	* dimension (`fleet-model.ts:33-38` `driftState`), so an enum makes the
	* contradiction unrepresentable instead of merely unsatisfiable.
	*
	* ⚠️ The member strings are BYTE-IDENTICAL to `DriftState`
	* (`cp-admin-ui/src/panes/fleet-model.ts:33-38`) so the console can pass its
	* own derived value straight through with no mapping table. The console
	* package cannot be imported here, so the equality is asserted by literal in
	* `__tests__/app-admin-inputs.test.ts` with that file cited.
	*/
	driftState: z.enum([
		"in-sync",
		"drifted",
		"no-desired"
	]).optional(),
	/**
	* B7 — the "Scaled to 0" tile.
	*
	* ⛔ A STRING ENUM, not `z.coerce.boolean()`. `z.coerce.boolean()` maps the
	* string `"false"` to `true`, so `?scaledToZero=false` would filter to the
	* exact opposite of what it says — the same trap
	* `machine-type-catalog.dto.ts:13-15` records for `refresh`, in the same
	* repo, for the same reason. ⛔ And not a `.transform()`:
	* `createOpenApiDocument` cannot represent one in JSON Schema (ibid.).
	*/
	scaledToZero: z.enum(["true", "false"]).optional(),
	/** Opaque, server-minted base64url keyset cursor from the previous page. */
	cursor: z.string().min(1).max(256).optional(),
	/** Query-string coercion: `"50"` → `50`. Bounded `[1, 200]`, default 50. */
	limit: z.coerce.number().int().min(1).max(200).default(50)
}).strict();
/**
* Query-string schema for **E11**, `GET /v1/admin/apps/:appId/history`.
*
* Bound at the controller via `@Query({ schema })`; the global
* `ZionStrictValidationPipe` rejects malformed input with a 400 before it reaches
* the service. That is load-bearing, not decorative: `?dpId=foo` would otherwise
* reach the SQL layer's `::uuid` cast and surface as a 500, and `?limit=foo`
* would produce `LIMIT NaN`.
*
* `.strict()` (#466) rejects unknown keys. §2.4's refusals apply here by
* construction — there is no `orgId`, no `namespace`, no query language, and the
* app scope comes from the PATH param plus the principal's grants, never from the
* query string.
*
* `since`'s BOUNDS are not enforced here. "Within the last 90 days" is relative
* to the request instant, which a static schema cannot express; `resolveWindow`
* owns it and raises a 400 with the reason. The schema owns only the SHAPE
* (a parseable ISO-8601 datetime).
*/
const appHistoryQuerySchema = z.object({
	/** Restrict to one data plane. UUID-shaped — it is cast `::uuid` in the SQL. */
	dpId: z.string().uuid().optional(),
	/** Restrict to one fixture id. */
	fixtureId: z.string().min(1).max(63).optional(),
	/** ISO-8601 lower bound. Defaults to now − 7d; rejected beyond 90d (§2.4). */
	since: z.string().datetime({ offset: true }).optional(),
	/** Query-string coercion: `"250"` → `250`. Bounded `[1, 500]`, default 200. */
	limit: z.coerce.number().int().min(1).max(500).default(200),
	/** Opaque base64url keyset cursor from the previous page's `nextCursor`. */
	cursor: z.string().min(1).max(512).optional()
}).strict();
//#endregion
export { ADOPTION_DEFAULT_LIMIT, ADOPTION_MAX_LIMIT, APP_ID_UUID_PATTERN, APP_ROLE_SLUGS, DEPLOYMENTS_DEFAULT_LIMIT, DEPLOYMENTS_MAX_LIMIT, FIXTURE_ID_PATTERN, HISTORY_DEFAULT_LIMIT, HISTORY_MAX_LIMIT, LIBRARY_INSTANCE_SLUG_RESERVED_MESSAGE, MAX_VERSION_SLUG_LENGTH, RESERVED_APPS_ROUTE_SLUGS, RESERVED_APPS_ROUTE_SLUG_MESSAGE, RESERVED_APP_SLUG_MESSAGE, RESERVED_PLATFORM_PREFIX_SLUG_MESSAGE, SLUG_MESSAGE, SLUG_PATTERN, UUID_SHAPED_APP_SLUG_MESSAGE, VERSION_SLUG_FILTER_PATTERN, addAppOwnerSchema, adoptionQuerySchema, appHistoryQuerySchema, appRoleSlugEnum, createAppSchema, deploymentHealthEnum, deploymentsQuerySchema, healthEnum, ringEnum, updateAppSchema };

//# sourceMappingURL=app-admin-inputs.js.map